import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import Swal from "sweetalert2";
import {
  entregaService,
  EntregaFilters,
  Entrega,
} from "../../../../services/entregaService";
import { lojaService } from "../../../../services/lojaService";
import { usuarioService } from "../../../../services/usuarioService";
import { BrazilDate } from "../../../../utils/BrazilDate";
import { useAuth } from "../../../../context/AuthContext";
import { hasFullAccessUser } from "../../../../lib/screenAccess";
import { useDeleteEntrega } from "../../../../hooks/useEntrega";
import { COLORS } from "../../../../constants";

interface HistoricoEntregaContextData {
  entregas: Entrega[];
  isLoading: boolean;
  startDate: string;
  endDate: string;
  selectedLojaId: number | null;
  selectedUsuarioId: number | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setSelectedLojaId: (id: number | null) => void;
  setSelectedUsuarioId: (id: number | null) => void;
  setCurrentPage: (page: number) => void;
  handleApplyFilter: () => void;
  handleDelete: (rowIndex: number) => Promise<void>;
  isAdministrator: boolean;
  lojaOptions: Array<{ value: number; label: string }>;
  usuarioOptions: Array<{ value: number; label: string }>;
  formatDate: (dateString: string | null) => string;
  formatTime: (seconds: number | null) => string;
}

interface HistoricoEntregaProviderProps {
  children: React.ReactNode;
}

const HistoricoEntregaContext = createContext<HistoricoEntregaContextData>(
  {} as HistoricoEntregaContextData
);

export const HistoricoEntregaProvider: React.FC<
  HistoricoEntregaProviderProps
> = ({ children }) => {
  const { user } = useAuth();
  const deleteMutation = useDeleteEntrega();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLojaId, setSelectedLojaId] = useState<number | null>(null);
  const [selectedUsuarioId, setSelectedUsuarioId] = useState<number | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState<EntregaFilters>({});
  const itemsPerPage = 10;

  const isAdministrator = hasFullAccessUser(user);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const { data: usuariosResponse } = useQuery({
    queryKey: ["usuarios", "options"],
    queryFn: () => usuarioService.getUsuarios({ limit: 1000 }),
  });
  const usuarios = useMemo(
    () => usuariosResponse?.data ?? [],
    [usuariosResponse?.data]
  );

  const { data: paginatedData, isLoading } = useQuery({
    queryKey: [
      "entregas-historicos",
      appliedFilters.lojaId,
      appliedFilters.entregadorId,
      currentPage,
      appliedFilters.dataInicio,
      appliedFilters.dataFim,
    ],
    queryFn: () =>
      entregaService.getAll({
        lojaId: appliedFilters.lojaId || undefined,
        entregadorId: appliedFilters.entregadorId || undefined,
        page: currentPage,
        limit: itemsPerPage,
        dataInicio: appliedFilters.dataInicio,
        dataFim: appliedFilters.dataFim,
      }),
  });

  const entregas = paginatedData?.data || [];
  const totalPages = paginatedData?.totalPages || 0;
  const totalItems = paginatedData?.total || 0;

  const lojaOptions = lojas.map((loja) => ({
    value: loja.id,
    label: loja.nome,
  }));

  const isEntregadorRole = (nome: string | undefined) =>
    nome === "Entregador" || nome === "Entregadores";

  const usuarioOptions = usuarios
    .filter((u) => isEntregadorRole(u.role?.nome))
    .filter((u) => !selectedLojaId || u.lojaId === selectedLojaId)
    .map((usuario) => ({
      value: usuario.id,
      label: usuario.nome,
    }));

  useEffect(() => {
    if (selectedLojaId == null || selectedUsuarioId == null) return;
    const user = usuarios.find((u) => u.id === selectedUsuarioId);
    const isEntregador = isEntregadorRole(user?.role?.nome);
    const belongsToSelectedLoja = user && user.lojaId === selectedLojaId;
    if (!isEntregador || !belongsToSelectedLoja) {
      setSelectedUsuarioId(null);
    }
  }, [selectedLojaId, selectedUsuarioId, usuarios]);

  const handleApplyFilter = () => {
    setCurrentPage(1);
    setAppliedFilters({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId: selectedLojaId,
      entregadorId: selectedUsuarioId,
    });
  };

  const formatDate = (dateString: string | null) => {
    return BrazilDate.formatBRNaive(dateString, true);
  };

  const formatTime = (seconds: number | null) => {
    if (!seconds) return "-";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleDelete = async (rowIndex: number) => {
    const entrega = entregas[rowIndex];
    if (!entrega) return;
    const result = await Swal.fire({
      title: "Excluir registro?",
      text: "Esta ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonColor: COLORS.PRIMARY,
      confirmButtonText: "Sim, excluir!",
    });
    if (result.isConfirmed) {
      try {
        await deleteMutation.mutateAsync(entrega.id);
        await Swal.fire({
          title: "Excluído",
          text: "Registro excluído com sucesso.",
          icon: "success",
          confirmButtonColor: COLORS.PRIMARY,
        });
      } catch {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível excluir o registro.",
          icon: "error",
          confirmButtonColor: COLORS.PRIMARY,
        });
      }
    }
  };

  return (
    <HistoricoEntregaContext.Provider
      value={{
        entregas,
        isLoading,
        startDate,
        endDate,
        selectedLojaId,
        selectedUsuarioId,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        setStartDate,
        setEndDate,
        setSelectedLojaId,
        setSelectedUsuarioId,
        setCurrentPage,
        handleApplyFilter,
        handleDelete,
        isAdministrator,
        lojaOptions,
        usuarioOptions,
        formatDate,
        formatTime,
      }}
    >
      {children}
    </HistoricoEntregaContext.Provider>
  );
};

export const useHistoricoEntrega = () => {
  const context = useContext(HistoricoEntregaContext);
  if (!context) {
    throw new Error(
      "useHistoricoEntrega deve ser usado dentro de HistoricoEntregaProvider"
    );
  }
  return context;
};
